/**
 * Laravel Echo client factory.
 *
 * Reverb is Pusher-protocol-compatible. The portal connects via wss://
 * to /broadcasting/* on app.nithi.co.in (host nginx already routes that
 * path to the reverb container at 127.0.0.1:9105).
 *
 * Channel auth: when subscribing to private-* channels, Echo automatically
 * POSTs to /broadcasting/auth which Laravel handles via Sanctum cookie auth.
 *
 * Singleton — one connection per app. Disconnect on logout.
 */

import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

let echoInstance: Echo<'reverb'> | null = null;

declare global {
  interface Window {
    Pusher: typeof Pusher;
  }
}

export interface EchoConfig {
  appKey: string;
  host: string;
  port: number;
  scheme: 'http' | 'https' | 'ws' | 'wss';
}

export function connectEcho(config: EchoConfig): Echo<'reverb'> {
  if (echoInstance) return echoInstance;

  // Pusher must be on window for laravel-echo to find it.
  window.Pusher = Pusher;

  echoInstance = new Echo({
    broadcaster: 'reverb',
    key: config.appKey,
    wsHost: config.host,
    wssHost: config.host,
    wsPort: config.port,
    wssPort: config.port,
    forceTLS: config.scheme === 'wss' || config.scheme === 'https',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: '/broadcasting/auth',
    auth: {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    },
  });

  return echoInstance;
}

export function disconnectEcho(): void {
  if (echoInstance) {
    echoInstance.disconnect();
    echoInstance = null;
  }
}

export function getEcho(): Echo<'reverb'> | null {
  return echoInstance;
}
